"use client";

import type { LocationDeal } from "~/graphql-client/generated";
import { dealToProduct } from "~/hooks/deals";
import type {
  CartItem,
  CartItemDetailed,
  Price,
  Product,
  ProductCategory,
  ProductOption,
} from "~/types";

interface Result {
  cartDetails: CartItemDetailed[];
  cartTotalPrice: Price;
  itemCount: number;
  hasUnavailableItems: boolean;
}

export function mapCartDetails(
  cartItems: CartItem[],
  menu: ProductCategory[],
  deals: LocationDeal[],
): Result {
  if (!menu.length) {
    return {
      cartDetails: [],
      cartTotalPrice: { amount: 0, taxIncluded: 0 },
      itemCount: 0,
      hasUnavailableItems: false,
    };
  }

  const cartDetails = cartItems.map((item) => {
    if (item.isDeal) {
      const dealProduct = getDealAsProduct(deals, menu, item);
      return getCartItemDetails(item, dealProduct);
    }

    const product = getProduct(menu, item);
    return getCartItemDetails(item, product);
  });

  const cartTotalPrice = cartDetails.reduce(
    (totalPrice, item) => {
      if (!item.product) return totalPrice;
      const itemTotal = getCartItemTotal(item, item.product);
      totalPrice.amount = totalPrice.amount + itemTotal.amount;
      totalPrice.taxIncluded += itemTotal.taxIncluded ?? 0;
      return totalPrice;
    },
    { amount: 0, taxIncluded: 0 },
  );

  const hasUnavailableItems = cartDetails.some((item) => {
    return !isCartItemAvailable(item);
  });

  const itemCount = cartDetails.reduce(
    (total, item) => total + item.quantity,
    0,
  );

  return {
    cartDetails,
    cartTotalPrice,
    itemCount,
    hasUnavailableItems,
  };
}

function getProduct(
  categories: ProductCategory[],
  cartItem: CartItem,
): Product | undefined {
  return categories
    .flatMap((cat) => cat.products || [])
    .find((product) => product.id === cartItem.productId);
}

function getDealAsProduct(
  deals: LocationDeal[],
  categories: ProductCategory[],
  cartItem: CartItem,
): Product | undefined {
  const deal = deals.find((d) => d.id === cartItem.productId);

  if (!deal) return undefined;

  const products = categories.flatMap((cat) => cat.products || []);

  return dealToProduct(deal, products);
}

export function getCartItemDetails(
  cartItem: CartItem,
  product?: Product,
): CartItemDetailed {
  const { lineItemId, productId, quantity, note } = cartItem;

  if (!product) {
    return {
      lineItemId,
      productId,
      quantity,
      hasUnavailableOptions: false,
      note,
      options: [],
    };
  }

  let hasUnavailableOptions = false;

  const options = cartItem.options.map((o) => {
    const option = findMatchingOption(product.options || [], o.optionId);

    if (!option) {
      hasUnavailableOptions = true;
    }

    let hasUnavailableChoices = false;

    const choices = o.choices.map((c) => {
      const choiceProduct = option && findMatchingProduct(option, c.productId);

      if (!choiceProduct) {
        hasUnavailableChoices = true;
      }

      return getCartItemDetails(c, choiceProduct);
    });

    return {
      title: option?.title || "",
      optionId: o.optionId,
      choices,
      hasUnavailableChoices,
    };
  });

  return {
    lineItemId,
    productId,
    quantity,
    note,
    product,
    options,
    hasUnavailableOptions,
  };
}

function findMatchingProduct(option: ProductOption, choiceProductId: string) {
  const choice = option.choices.find((c) => c.product.id === choiceProductId);

  return choice?.product;
}

function findMatchingOption(options: ProductOption[], optionId: string) {
  return options.find((o) => o.id === optionId);
}

interface Option {
  optionId: string;
  choices: Choice[];
}
interface Choice {
  productId: string;
  quantity: number;
  options: Option[];
}

export function getCartItemTotal(
  cartItem: {
    quantity: number;
    options: Option[];
  },
  product?: Product,
): Price {
  // loading
  if (!product) return { amount: 0, taxIncluded: 0 };

  let total = cartItem.quantity * product.price.amount;
  let totalTaxIncluded = cartItem.quantity * (product.price.taxIncluded ?? 0);

  const optionTotals = cartItem.options.reduce(
    (optionTotalPrice, o) => {
      o.choices.forEach((c) => {
        const option = findMatchingOption(product.options || [], o.optionId);
        const nestedProduct =
          option && findMatchingProduct(option, c.productId);
        if (nestedProduct) {
          const nestedPrice = getCartItemTotal(c, nestedProduct);
          optionTotalPrice.amount =
            optionTotalPrice.amount + nestedPrice.amount;
          optionTotalPrice.taxIncluded =
            optionTotalPrice.taxIncluded + (nestedPrice.taxIncluded ?? 0);
        }
      });

      return optionTotalPrice;
    },
    { amount: 0, taxIncluded: 0 },
  );

  total = total + optionTotals.amount * cartItem.quantity;
  totalTaxIncluded += optionTotals.taxIncluded * cartItem.quantity;

  return { amount: total, taxIncluded: totalTaxIncluded };
}

function isCartItemAvailable(cartItem: CartItemDetailed): boolean {
  if (!cartItem.product) return false;
  if (cartItem.product.available === false) return false;
  if (cartItem.hasUnavailableOptions) return false;

  let available = true;
  cartItem.options.forEach((o) => {
    o.choices.forEach((c) => {
      if (!isCartItemAvailable(c)) {
        available = false;
      }
    });
  });

  return available;
}
